import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Plus, Trash2, Play, Archive, Save, ArrowLeft, Zap, History, X } from 'lucide-react'
import { lanEditionsAPI } from '@/lib/api'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export default function LanEditionsAdminPage() {
  const [list, setList] = useState([])
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [archive, setArchive] = useState(null) // {edition, data}

  const load = () => lanEditionsAPI.list().then(r => setList(r.data.editions || []))
  useEffect(() => { load() }, [])

  const create = async () => {
    if (!draft?.name) return
    setSaving(true)
    try {
      if (draft.id) await lanEditionsAPI.update(draft.id, draft)
      else await lanEditionsAPI.create(draft)
      setDraft(null); await load()
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)) }
    finally { setSaving(false) }
  }

  const activate = async (e) => {
    if (!confirm(`Editie "${e.name}" activeren?\n\nAlle huidige actieve editie wordt gearchiveerd (data blijft). Nieuwe shows/toernooien worden automatisch aan de nieuwe editie gekoppeld.`)) return
    try { await lanEditionsAPI.activate(e.id); await load() }
    catch (err) { alert('Fout: ' + (err.response?.data?.error || err.message)) }
  }

  const remove = async (e) => {
    if (!confirm(`"${e.name}" verwijderen?`)) return
    try { await lanEditionsAPI.delete(e.id); await load() }
    catch (err) { alert('Fout: ' + (err.response?.data?.error || err.message)) }
  }

  const openArchive = async (e) => {
    setArchive({ edition: e, data: null, loading: true })
    try {
      const r = await lanEditionsAPI.archive(e.id)
      setArchive({ edition: e, data: r.data, loading: false })
    } catch (err) {
      setArchive({ edition: e, data: null, loading: false, error: err.response?.data?.error || err.message })
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Calendar}
        eyebrow="Beheer · LAN"
        title="LAN edities"
        subtitle="Elke LAN krijgt een eigen editie. Bij activeren wordt de vorige gearchiveerd — data blijft bewaard, alleen niet meer actief."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/admin/lan-wizard">
                <Plus className="h-4 w-4 mr-2" />Wizard (aanbevolen)
              </Link>
            </Button>
            <Button variant="outline" onClick={async () => {
              // Twee-fase-confirm zodat je een live-LAN nooit per ongeluk
              // archiveert. De eerste vraagt of je zeker weet dat er GEEN
              // actieve LAN loopt; alleen dan mag je door.
              const zeker = confirm(
                'POPULATE TEST-LAN\n\n' +
                'Dit maakt een verse test-editie aan: nieuwe LAN, KV-show, image-compo, ' +
                'toernooi, deelnemers, teams, tickets, bar-data.\n\n' +
                '⚠️ De huidige actieve LAN wordt gearchiveerd. Als er nu een echte LAN loopt, ' +
                'breekt dat overlays, kassa-koppeling en scherm-regie.\n\n' +
                'Weet je zeker dat er nu GEEN echte LAN actief is?'
              )
              if (!zeker) return
              const bevestig = prompt('Typ TEST om te bevestigen:')
              if (bevestig?.trim().toUpperCase() !== 'TEST') { alert('Afgebroken — bevestiging klopt niet.'); return }
              try { const r = await lanEditionsAPI.populateTest(); alert(r.data.message || 'OK'); location.reload() }
              catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)) }
            }}>
              <Zap className="h-4 w-4 mr-2 text-acid" />Test LAN
            </Button>
            <Button onClick={() => setDraft({ name: '', description: '', starts_at: '', ends_at: '' })}>
              <Plus className="h-4 w-4 mr-2" />Nieuwe editie
            </Button>
            <Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>
          </>
        }
      />

      {draft && (
        <Card className="border-acid/40">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Naam</span>
                <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm"
                       placeholder="Bijv. Bear & Bockie LAN · Editie 13" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Beschrijving</span>
                <input value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Startdatum</span>
                <input type="date" value={draft.starts_at || ''} onChange={e => setDraft({ ...draft, starts_at: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Einddatum</span>
                <input type="date" value={draft.ends_at || ''} onChange={e => setDraft({ ...draft, ends_at: e.target.value })}
                       className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-border/40 pt-3">
              <Button variant="outline" onClick={() => setDraft(null)}>Annuleren</Button>
              <Button onClick={create} disabled={saving || !draft.name}>
                <Save className="h-4 w-4 mr-2" />Opslaan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 divide-y divide-border/40">
          {list.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">Nog geen edities.</div>
          )}
          {list.map(e => (
            <div key={e.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-3"
                 style={e.is_active ? { borderLeft: '2px solid rgb(var(--acid-rgb))' } : {}}>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {e.name}
                  {e.is_active && <span className="font-mono text-[10px] uppercase tracking-widest text-acid">actief</span>}
                  {e.archived_at && !e.is_active && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                      <Archive className="h-3 w-3" />gearchiveerd
                    </span>
                  )}
                </div>
                {e.description && <div className="text-xs text-muted-foreground truncate">{e.description}</div>}
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  {e.starts_at && `${e.starts_at}`}
                  {e.starts_at && e.ends_at && ' → '}
                  {e.ends_at && e.ends_at}
                  {' · '}
                  {e.tournament_count} toernooien · {e.vision_show_count} KV · {e.imagecompo_show_count} IC shows
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => openArchive(e)} title="Bekijk archief">
                <History className="h-4 w-4 mr-1" />Archief
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDraft(e)}>Aanpassen</Button>
              {!e.is_active && (
                <Button size="sm" onClick={() => activate(e)}>
                  <Play className="h-4 w-4 mr-1" />Activeer
                </Button>
              )}
              {e.is_active && <div className="w-[92px]" />}
              <Button size="sm" variant="outline" onClick={() => remove(e)}>
                <Trash2 className="h-4 w-4 text-signal" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-border" style={{ borderLeft: '2px solid rgb(var(--peach-rgb))' }}>
        <CardContent className="p-4 text-sm text-muted-foreground">
          <b className="text-peach">Hoe werkt dit:</b> Users, badges, wiki en prijs-catalogus blijven persistent over edities.
          Tournaments, shows, punten, prijs-claims, aankondigingen, polls, chat en notificaties worden per editie gescoped —
          bij een nieuwe LAN zie je fresh data, maar de historie van vorige edities blijft compleet raadpleegbaar via de
          "Archief"-knop per editie.
        </CardContent>
      </Card>

      {/* Archief-modal: per-editie overzicht van wat er tijdens die LAN gebeurd is. */}
      {archive && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4"
             onClick={() => setArchive(null)}>
          <Card className="max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Archief</div>
                <div className="font-semibold">{archive.edition.name}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setArchive(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="overflow-y-auto p-4 space-y-4">
              {archive.loading && <div className="text-muted-foreground text-sm">Laden…</div>}
              {archive.error && <div className="text-signal text-sm">{archive.error}</div>}
              {archive.data && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <ArchiveTile label="Toernooien"     value={archive.data.tournaments?.length ?? 0} />
                    <ArchiveTile label="Prijs-claims"   value={archive.data.prize_claims?.n ?? 0} sub={`${archive.data.prize_claims?.punten ?? 0} pt`} />
                    <ArchiveTile label="Punten uitgereikt" value={archive.data.points_awarded?.totaal ?? 0} />
                    <ArchiveTile label="Compo-uitslagen" value={archive.data.compo_results?.length ?? 0} />
                    <ArchiveTile label="Notificaties"   value={archive.data.notifications?.n ?? 0} />
                    <ArchiveTile label="Chat-messages"  value={archive.data.chat_messages?.n ?? 0} />
                    <ArchiveTile label="Aankondigingen" value={archive.data.announcements?.length ?? 0} />
                    <ArchiveTile label="Polls"          value={archive.data.polls?.length ?? 0} />
                  </div>
                  {archive.data.tournaments?.length > 0 && (
                    <ArchiveSection title="Toernooien">
                      {archive.data.tournaments.map(t => (
                        <div key={t.id} className="flex items-center justify-between text-sm py-1">
                          <span>{t.name} <span className="text-muted-foreground text-xs">· {t.game}</span></span>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.status}</span>
                        </div>
                      ))}
                    </ArchiveSection>
                  )}
                  {archive.data.compo_results?.length > 0 && (
                    <ArchiveSection title="Compo-uitslagen">
                      {archive.data.compo_results.slice(0, 20).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-1">
                          <span>#{r.place} {r.display_name} <span className="text-muted-foreground text-xs">· {r.source_name}</span></span>
                          <span className="text-xs text-muted-foreground">{r.points} pt</span>
                        </div>
                      ))}
                    </ArchiveSection>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

function ArchiveTile({ label, value, sub }) {
  return (
    <div className="rounded border border-border bg-card/60 px-3 py-2">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

function ArchiveSection({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
      <div className="border border-border rounded divide-y divide-border/40 px-3 py-1">{children}</div>
    </div>
  )
}
